import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Checkbox, Col, Form, Input, Modal, Row, Space, Switch, Table, Tag, Typography } from 'antd';
import { PlusOutlined, ReloadOutlined, SettingOutlined, TeamOutlined } from '@ant-design/icons';
import MainLayout from '../../components/MainLayout';
import { ApiServices } from '../../services/apiServices';
import { ApiConfig } from '../../services/ApiConfigs';
import { errorMsg, successMsg } from '../../utils/ShowMessage';
import { useSelector } from 'react-redux';

const { Title, Text } = Typography;

const PERMISSION_GROUPS = [
  { title: 'Dashboard', items: [{ key: 'dashboard.read', label: 'View dashboard' }] },
  {
    title: 'Orders',
    items: [
      { key: 'orders.read', label: 'View orders' },
      { key: 'orders.create', label: 'Create orders' },
      { key: 'orders.update', label: 'Update orders' },
      { key: 'orders.assign', label: 'Assign driver / shipment actions' },
      { key: 'orders.payment', label: 'Update payment type' },
      { key: 'orders.archive', label: 'Archive / unarchive orders' },
      { key: 'orders.delete', label: 'Delete orders' },
    ]
  },
  {
    title: 'Drivers',
    items: [
      { key: 'drivers.read', label: 'View drivers' },
      { key: 'drivers.create', label: 'Create drivers' },
      { key: 'drivers.update', label: 'Update drivers' },
      { key: 'drivers.delete', label: 'Delete drivers' },
    ]
  },
  {
    title: 'Warehouse',
    items: [
      { key: 'warehouse.read', label: 'View warehouses' },
      { key: 'warehouse.create', label: 'Create warehouses' },
      { key: 'warehouse.update', label: 'Update warehouses' },
      { key: 'warehouse.delete', label: 'Delete warehouses' },
    ]
  },
  { title: 'Shipments', items: [{ key: 'shipments.read', label: 'View shipments' }, { key: 'shipments.write', label: 'Manage shipments' }] },
  { title: 'Live Tracking', items: [{ key: 'live_tracking.read', label: 'View live tracking' }] },
  {
    title: 'Notifications',
    items: [
      { key: 'notifications.read', label: 'View notifications' },
      { key: 'notifications.write', label: 'Send notifications' },
      { key: 'notifications.delete', label: 'Delete notifications' },
    ]
  },
  { title: 'Settings', items: [{ key: 'settings.read', label: 'View settings' }, { key: 'settings.write', label: 'Manage settings' }] },
  { title: 'API Docs', items: [{ key: 'api_docs.read', label: 'View API docs' }] },
];

export default function Staff() {
  const userDetail = useSelector((s) => s.UserData.userData);
  const isStoreOwner = userDetail?.user?.role === 'store_owner' || userDetail?.role === 'user';
  const isSuperAdmin = userDetail?.role === 'superAdmin';

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const res = await ApiServices('get', ApiConfig.STAFF);
      if (res?.success) setData(res.data || []);
      else errorMsg(res?.message || 'Failed to load staff');
    } catch (e) {
      errorMsg(e?.message || 'Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStaff(); }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      status: true,
      permissions: {},
    });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.resetFields();
    form.setFieldsValue({
      firstname: row.firstname,
      lastname: row.lastname,
      email: row.email,
      phoneNumber: row.phoneNumber,
      status: !!row.status,
      permissions: row.permissions || {},
      password: '',
    });
    setOpen(true);
  };

  const [searchText, setSearchText] = useState('');

  const filteredData = useMemo(() => {
    if (!searchText) return data;
    const q = searchText.toLowerCase();
    return data.filter(
      (r) =>
        (r.firstname && r.firstname.toLowerCase().includes(q)) ||
        (r.lastname && r.lastname.toLowerCase().includes(q)) ||
        (r.email && r.email.toLowerCase().includes(q)) ||
        (r.phoneNumber && r.phoneNumber.toLowerCase().includes(q))
    );
  }, [data, searchText]);

  const columns = useMemo(() => ([
    {
      title: 'Staff Member',
      dataIndex: 'firstname',
      key: 'name',
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      render: (_, r) => (
        <Space size={10}>
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
              color: '#2563eb',
              border: '1.5px solid #bfdbfe',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 14,
            }}
          >
            {(r.firstname?.[0] || 'S').toUpperCase()}
          </div>
          <div>
            <Text strong style={{ fontSize: 14, color: '#0f172a' }}>
              {`${r.firstname || ''} ${r.lastname || ''}`.trim() || '—'}
            </Text>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              Staff User
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Email Address',
      dataIndex: 'email',
      key: 'email',
      responsive: ['sm', 'md', 'lg', 'xl'],
      render: (v) => <span style={{ color: '#334155', fontWeight: 500 }}>{v || '—'}</span>,
    },
    {
      title: 'Phone Number',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      responsive: ['md', 'lg', 'xl'],
      render: (v) => <span style={{ color: '#64748b' }}>{v || '—'}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      render: (v) =>
        Number(v) === 1 || v === true ? (
          <Tag color="success" style={{ borderRadius: 6, fontWeight: 600, padding: '2px 10px' }}>
            Active
          </Tag>
        ) : (
          <Tag color="error" style={{ borderRadius: 6, fontWeight: 600, padding: '2px 10px' }}>
            Inactive
          </Tag>
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 90,
      align: 'right',
      fixed: 'right',
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      render: (_, r) => (
        <Space>
          <Button
            size="middle"
            onClick={() => openEdit(r)}
            icon={<SettingOutlined />}
            style={{ borderRadius: 8, fontWeight: 500 }}
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ]), []);

  const submit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        firstname: values.firstname,
        lastname: values.lastname,
        email: values.email,
        phoneNumber: values.phoneNumber,
        status: values.status ? 1 : 0,
        permissions: values.permissions || {},
      };
      if (values.password) payload.password = values.password;

      const res = editing
        ? await ApiServices('put', `${ApiConfig.STAFF}/${editing.id}`, payload)
        : await ApiServices('post', ApiConfig.STAFF, { ...payload, password: values.password });

      if (res?.success) {
        successMsg(editing ? 'Staff updated' : 'Staff created');
        setOpen(false);
        fetchStaff();
      } else {
        errorMsg(res?.message || 'Failed');
      }
    } catch (e) {
      if (e?.message) errorMsg(e.message);
    }
  };

  if (!isStoreOwner && !isSuperAdmin) {
    return (
      <MainLayout>
        <Card>
          <Text>You don’t have access to Staff management.</Text>
        </Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div style={{ width: '100%' }}>
        {/* Header section matching Drivers & Warehouse */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16,
            marginBottom: 20,
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: 22,
                fontWeight: 700,
                color: '#0f172a',
                letterSpacing: '-0.02em',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <TeamOutlined style={{ color: '#2563eb' }} /> Staff Management
            </h2>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 3 }}>
              Create staff users, manage account access, and configure granular module permissions
            </div>
          </div>
          <Space size={10} wrap>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchStaff}
              loading={loading}
              size="large"
              style={{ borderRadius: 8, fontWeight: 500 }}
            >
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreate}
              size="large"
              style={{
                borderRadius: 8,
                fontWeight: 600,
                boxShadow: '0 2px 6px rgba(37, 99, 235, 0.25)',
              }}
            >
              Add Staff
            </Button>
          </Space>
        </div>

        {/* Search Bar */}
        <div style={{ marginBottom: 16, maxWidth: 360 }}>
          <Input.Search
            placeholder="Search staff by name, email or phone..."
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            size="middle"
            style={{ width: '100%', borderRadius: 8 }}
          />
        </div>

        {/* Table Container full width with responsive design */}
        <div
          style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: 12,
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
            width: '100%',
          }}
        >
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={filteredData}
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} staff`,
              style: { padding: '12px 20px', margin: 0 },
            }}
            scroll={{ x: 750 }}
          />
        </div>
      </div>


        <Modal
          title={editing ? 'Edit Staff' : 'Add Staff'}
          open={open}
          onCancel={() => setOpen(false)}
          onOk={submit}
          okText={editing ? 'Update' : 'Create'}
          width={900}
          destroyOnHidden
        >
          <Form form={form} layout="vertical">
            <Row gutter={12}>
              <Col xs={24} md={8}>
                <Form.Item name="firstname" label="First name" rules={[{ required: true }]}>
                  <Input placeholder="First name" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="lastname" label="Last name" rules={[{ required: true }]}>
                  <Input placeholder="Last name" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="phoneNumber" label="Phone">
                  <Input placeholder="Phone number" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
                  <Input placeholder="Email" disabled={!!editing} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="password"
                  label={editing ? 'Password (optional)' : 'Password'}
                  rules={editing ? [] : [{ required: true }]}
                >
                  <Input.Password placeholder={editing ? 'Leave empty to keep current' : 'Password'} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item name="status" label="Active" valuePropName="checked">
                  <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                </Form.Item>
              </Col>
            </Row>

            <div style={{ marginTop: 8, marginBottom: 6, fontWeight: 700, color: '#1e293b' }}>Permissions</div>
            <Row gutter={[12, 12]}>
              {PERMISSION_GROUPS.map((g) => (
                <Col key={g.title} xs={24} md={12}>
                  <Card size="small" style={{ borderRadius: 12, background: '#fafafa' }} title={g.title}>
                    {g.items.map((it) => (
                      <Form.Item key={it.key} name={['permissions', it.key]} valuePropName="checked" style={{ marginBottom: 8 }}>
                        <Checkbox>{it.label}</Checkbox>
                      </Form.Item>
                    ))}
                  </Card>
                </Col>
              ))}
            </Row>
          </Form>
        </Modal>
    </MainLayout>
  );
}

